import { convertToStoryblokCdn } from '../../pipes/storyblok-cdn.pipe';
import { StoryblokImage } from '../../storyblok-types';

export type StoryblokVideoTrack = {
  _uid: string;
  kind: string;
  vtt?: {
    filename?: string;
  };
  src?: string;
  srclang?: string;
  default?: boolean;
};

export type StoryblokVideoDimension = number | string | undefined;

/**
 * MIME type of the `videoOptimized` / `mobileVideoOptimized` renditions.
 * Currently HEVC Main profile, level 5.0 (hvc1 tag — required for Safari).
 * Chromium rejects a bare `hvc1` and needs the full profile/level string;
 * Safari accepts both. When the optimized codec changes (e.g. to AV1),
 * re-upload the assets and update this one constant.
 */
export const OPTIMIZED_VIDEO_TYPE = 'video/mp4; codecs=hvc1.1.6.L150.B0';

/**
 * MIME type of the H.264 `video` / `mobileVideo` fallbacks (High profile; the
 * level is a hint, browsers probe the file). Without `codecs`, Chromium builds
 * that lack H.264 (Lighthouse/PSI, Puppeteer) download every fallback source
 * before rejecting it — 19.6 MB per homepage audit.
 */
export const FALLBACK_VIDEO_TYPE = 'video/mp4; codecs=avc1.640028';

export type StoryblokVideoItem = {
  _uid?: string;
  component?: string;
  video?: StoryblokImage;
  mobileVideo?: StoryblokImage;
  videoOptimized?: StoryblokImage;
  mobileVideoOptimized?: StoryblokImage;
  width?: StoryblokVideoDimension;
  height?: StoryblokVideoDimension;
  poster?: StoryblokImage;
  videoTrack?: StoryblokVideoTrack[];
};

const hasFilename = (asset?: StoryblokImage): boolean => !!asset?.filename;

export const getVideoItem = (videoItem: StoryblokVideoItem[] | undefined): StoryblokVideoItem | undefined => {
  return videoItem?.find(
    item =>
      hasFilename(item?.video) ||
      hasFilename(item?.mobileVideo) ||
      hasFilename(item?.videoOptimized) ||
      hasFilename(item?.mobileVideoOptimized) ||
      hasFilename(item?.poster) ||
      !!item?.videoTrack?.length ||
      item?.width !== undefined ||
      item?.height !== undefined
  );
};

export const getResponsiveVideoItemAsset = (
  item: StoryblokVideoItem | undefined,
  isMobile: boolean
): StoryblokImage | undefined => {
  const desktopAsset = hasFilename(item?.video) ? item.video : undefined;
  const mobileAsset = hasFilename(item?.mobileVideo) ? item.mobileVideo : undefined;
  return isMobile && mobileAsset ? mobileAsset : desktopAsset || mobileAsset;
};

/** Single poster used on all viewports — should be a small rendition, it only bridges until the video's first frame. */
export const getVideoItemPoster = (item: StoryblokVideoItem | undefined): StoryblokImage | undefined =>
  hasFilename(item?.poster) ? item.poster : undefined;

/**
 * Storyblok image service rendition for the poster. `<video poster>` takes one URL for every
 * viewport, so this is sized for mobile (PSI: 380 CSS px at DPR 1.75 ≈ 665 px) and upscaled on
 * desktop for the sub-second until the video's first frame decodes. Upload the poster at full
 * size (2160 px); the CDN produces this rendition.
 */
export const POSTER_RENDITION = '720x0/filters:format(webp):quality(85)';

export const getVideoItemPosterUrl = (
  item: StoryblokVideoItem | undefined,
  storyblokUrl: string
): string | undefined => {
  const filename = getVideoItemPoster(item)?.filename;
  if (!filename) return undefined;
  const url = convertToStoryblokCdn(filename, storyblokUrl);
  return url.includes('/m/') ? url : `${url}/m/${POSTER_RENDITION}`;
};
